import React, { FC, ReactElement, Suspense } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { useAppSelector } from '../../hooks/defaultHooks';
import * as Sentry from '@sentry/react';
import routes from '../../routes';
import MainPage from '../../screens/MainPage';
import SettingsPage from '../../screens/SettingsPage';
import SignInPage from '../../screens/SignInPage';
import SignUpPage from '../../screens/SignUpPage';
import UsersPage from '../../screens/UsersPage';
import selectors from '../../selectors';
import ConsumersPage from '../../screens/ConsumersPage';
import MessagesPage from '../../screens/MessagesPage';
import MessagePage from '../../screens/MessagePage';
import ChatIndexRoute from '../message/ChatIndexRoute';
import LogsPage from '../../screens/LogsPage/LogsPage';
import ForgotPasswordPage from '../../screens/ForgotPasswordPage';
import ApplicationSettingsPage from '../../screens/ApplicationSettingsPage/ApplicationSettingsPage';
import { TariffsPageLazy } from '../../screens/TariffsPage';
import { TariffPageLazy } from '../../screens/TariffPage';
import NewTariffPage from '../../screens/NewTariffPage';
import NewTariffFeaturePage from '../../screens/NewTariffFearurePage';
import { UpdateTariffFeaturePageLazy } from '../../screens/UpdateTariffFeaturePage';
import ErrorPage from '../../screens/ErrorPage';
import { InvoicesPageLazy } from '../../screens/InvoicesPage';
import { ReferralRewardsPageLazy } from '../../screens/ReferralRewardsPage';
import Spinner from '../../UIComponents/Spinner';
import { UpdateTariffPageLazy } from '../../screens/UpdateTariffPage';
import { CreateAnnouncementPageLazy } from '../../screens/CreateAnnouncementPage';
import { UpdateAnnouncementPageLazy } from '../../screens/UpdateAnnouncementPage';
import { AdmsiteSettingsPageLazy } from '../../screens/AdmsiteSettingsPage';
import { PortalSettingsPageLazy } from '../../screens/PortalSettingsPage';
import SettingsStarterRouter from '../startRoutes/SettingsStarterRouter';
import { ToolsPageLazy } from '../../screens/ToolsPage';
import ToolsTab from '../tools/ToolsTab';
import ArtifactsTab from '../tools/ArtifactsTab';
import StylesTab from '../tools/StylesTab';
import ModelsTab from '../tools/ModelsTab';
import { ToolPageLazy } from '../../screens/ToolPage';
import { UpdateToolPageLazy } from '../../screens/UpdateToolPage';
import { ModelPageLazy } from '../../screens/ModelPage';
import { ModelFormPageLazy } from '../../screens/ModelFormPage';
import { StyleFormPageLazy } from '../../screens/StyleFormPage';
import { StylePromptFormPageLazy } from '../../screens/StylePromptFormPage';
import { StylePageLazy } from '../../screens/StylePage';
import { MobileSettingsPageLazy } from '../../screens/MobileSettingsPage';
import { CommonSettingsPageLazy } from '../../screens/CommonSettingsPage';
import { ReferallSettingsPageLazy } from '../../screens/ReferallSettingsPage';

interface RouterProps {
  children: ReactElement;
}

const PrivateRouter: FC<RouterProps> = ({ children }) => {
  const isAuth = useAppSelector(selectors.userAuth);
  return isAuth ? children : <Navigate to={routes.pages.signIn()} />;
};

const AuthRouter: FC<RouterProps> = ({ children }) => {
  const isAuth = useAppSelector(selectors.userAuth);
  return isAuth ? <Navigate to={routes.pages.mainPage()} /> : children;
};
// the wrapper is what feeds route names to Sentry's tracing for a data router
const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);
// TODO: refactor router
const router = sentryCreateBrowserRouter([
  {
    path: routes.pages.mainPage(),
    element: (
      <PrivateRouter>
        <MainPage />
      </PrivateRouter>
    ),
    errorElement: <ErrorPage />,
    children: [
      { index: true, element: <Navigate to={routes.pages.messagesPage()} replace={true} /> },
      {
        path: routes.pages.users(),
        element: <UsersPage />,
      },
      {
        path: routes.pages.invoicments(),
        element: (
          <Suspense fallback={<Spinner />}>
            <InvoicesPageLazy />
          </Suspense>
        ),
      },
      {
        path: routes.pages.referralRewardsPage(),
        element: (
          <Suspense fallback={<Spinner />}>
            <ReferralRewardsPageLazy />
          </Suspense>
        ),
      },
      {
        path: routes.pages.settings(),
        element: <SettingsPage />,
      },
      {
        path: routes.pages.consumers(),
        element: <ConsumersPage />,
      },
      {
        path: routes.pages.messagesPage(),
        element: <MessagesPage />,
        children: [
          { index: true, element: <ChatIndexRoute /> },
          {
            path: routes.pages.messagePage(),
            element: <MessagePage />,
          },
        ],
      },
      {
        path: routes.pages.logsPage(),
        element: <LogsPage />,
      },
      {
        path: routes.pages.tariffsPage(),
        element: (
          <Suspense fallback={<Spinner />}>
            <TariffsPageLazy />
          </Suspense>
        ),
      },
      {
        path: routes.pages.tariffPage(),
        element: (
          <Suspense fallback={<Spinner />}>
            <TariffPageLazy />
          </Suspense>
        ),
      },
      {
        path: routes.pages.newTariffPage(),
        element: <NewTariffPage />,
      },
      {
        path: routes.pages.updateTariffPage(),
        element: (
          <Suspense fallback={<Spinner />}>
            <UpdateTariffPageLazy />
          </Suspense>
        ),
      },
      {
        path: routes.pages.newTariffFeaturePage(),
        element: <NewTariffFeaturePage />,
      },
      {
        path: routes.pages.updateTariffFeaturePage(),
        element: (
          <Suspense>
            <UpdateTariffFeaturePageLazy />
          </Suspense>
        ),
      },
      {
        path: routes.pages.applicationSettingsPage(),
        element: <ApplicationSettingsPage />,
        children: [
          {
            index: true,
            element: <SettingsStarterRouter />,
          },
          {
            path: routes.pages.applicationSettingsPageForAdmin(),
            element: (
              <Suspense fallback={<Spinner />}>
                <AdmsiteSettingsPageLazy />
              </Suspense>
            ),
          },
          {
            path: routes.pages.applicationSettingsPageForPortal(),
            element: (
              <Suspense fallback={<Spinner />}>
                <PortalSettingsPageLazy />
              </Suspense>
            ),
          },
          {
            path: routes.pages.applicationSettingsPageForMobile(),
            element: (
              <Suspense fallback={<Spinner />}>
                <MobileSettingsPageLazy />
              </Suspense>
            ),
          },
          {
            path: routes.pages.applicationSettingsPageForCommon(),
            element: (
              <Suspense fallback={<Spinner />}>
                <CommonSettingsPageLazy />
              </Suspense>
            ),
          },
          {
            path: routes.pages.applicationsReferallSettingsPage(),
            element: (
              <Suspense>
                <ReferallSettingsPageLazy />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: routes.pages.createAnnouncementPage(),
        element: (
          <Suspense fallback={<Spinner />}>
            <CreateAnnouncementPageLazy />
          </Suspense>
        ),
      },
      {
        path: routes.pages.updateAnnouncementPage(),
        element: (
          <Suspense fallback={<Spinner />}>
            <UpdateAnnouncementPageLazy />
          </Suspense>
        ),
      },
      {
        path: routes.pages.toolsPage(),
        element: (
          <Suspense fallback={<Spinner />}>
            <ToolsPageLazy />
          </Suspense>
        ),
        children: [
          { index: true, element: <ToolsTab /> },
          { path: routes.pages.artifactsPage(), element: <ArtifactsTab /> },
          { path: routes.pages.stylesPage(), element: <StylesTab /> },
          { path: routes.pages.modelsPage(), element: <ModelsTab /> },
        ],
      },
      {
        path: routes.pages.newStylePage(),
        element: (
          <Suspense fallback={<Spinner />}>
            <StyleFormPageLazy />
          </Suspense>
        ),
      },
      {
        path: routes.pages.stylePage(),
        element: (
          <Suspense fallback={<Spinner />}>
            <StylePageLazy />
          </Suspense>
        ),
      },
      {
        path: routes.pages.updateStylePage(),
        element: (
          <Suspense fallback={<Spinner />}>
            <StyleFormPageLazy />
          </Suspense>
        ),
      },
      {
        path: routes.pages.stylePromptPage(),
        element: (
          <Suspense fallback={<Spinner />}>
            <StylePromptFormPageLazy readOnly />
          </Suspense>
        ),
      },
      {
        path: routes.pages.newStylePromptPage(),
        element: (
          <Suspense fallback={<Spinner />}>
            <StylePromptFormPageLazy />
          </Suspense>
        ),
      },
      {
        path: routes.pages.stylePromptVersionPage(),
        element: (
          <Suspense fallback={<Spinner />}>
            <StylePromptFormPageLazy />
          </Suspense>
        ),
      },
      {
        path: routes.pages.newModelPage(),
        element: (
          <Suspense fallback={<Spinner />}>
            <ModelFormPageLazy />
          </Suspense>
        ),
      },
      {
        path: routes.pages.modelPage(),
        element: (
          <Suspense fallback={<Spinner />}>
            <ModelPageLazy />
          </Suspense>
        ),
      },
      {
        path: routes.pages.updateModelPage(),
        element: (
          <Suspense fallback={<Spinner />}>
            <ModelFormPageLazy />
          </Suspense>
        ),
      },
      {
        path: routes.pages.toolPage(),
        element: (
          <Suspense fallback={<Spinner />}>
            <ToolPageLazy />
          </Suspense>
        ),
      },
      {
        path: routes.pages.updateToolPage(),
        element: (
          <Suspense fallback={<Spinner />}>
            <UpdateToolPageLazy />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: routes.pages.signIn(),
    element: (
      <AuthRouter>
        <SignInPage />
      </AuthRouter>
    ),
    index: true,
    errorElement: <ErrorPage />,
  },
  {
    path: routes.pages.signUp(),
    element: <SignUpPage />,
    errorElement: <ErrorPage />,
  },
  {
    path: routes.pages.forgotPassportPage(),
    element: <ForgotPasswordPage />,
    errorElement: <ErrorPage />,
  },
]);

const App: FC = () => (
  <Sentry.ErrorBoundary showDialog={true}>
    <RouterProvider router={router} />
  </Sentry.ErrorBoundary>
);
export default App;
